import { useState, useEffect, lazy, Suspense } from 'react';
import { SubscriptionGateProvider } from '../context/SubscriptionGateContext';
import Header from '../components/Header';
import HeroSection from '../components/HeroSection';
import ArticlesSection from '../components/ArticlesSection';
import Sidebar from '../components/Sidebar';
import BottomNav from '../components/BottomNav';
import AdBannerSection from '../components/AdBannerSection';
import Footer from '../components/Footer';
import PaperViewer from '../components/PaperViewer';
import AppDock from '../components/AppDock';
import MarketingPromoBar from '../components/MarketingPromoBar';
import SubscribePromoSection from '../components/SubscribePromoSection';
import EditionLiveBanner from '../components/EditionLiveBanner';
import SiteAnnouncement from '../components/SiteAnnouncement';
import ReferralPopup from '../components/ReferralPopup';
import TrustRibbon from '../components/TrustRibbon';
import GuestSignInBar from '../components/GuestSignInBar';
import EditionNotifyPrompt from '../components/EditionNotifyPrompt';
import { isNativeApp } from '../utils/platform.js';
import { useLang } from '../context/LanguageContext';
import { NativeTabProvider, useNativeTab } from '../mobile/NativeTabContext';
import NativeHeader from '../mobile/NativeHeader';
import NativeHomeScreen from '../mobile/NativeHomeScreen';
import NativeYouScreen from '../mobile/NativeYouScreen';
import NativePaperScreen from '../mobile/NativePaperScreen';
import NativeSearchSheet from '../mobile/NativeSearchSheet';

const YoutubeSection = lazy(() => import('../components/YoutubeSection'));
const NativeVideosScreen = lazy(() => import('../mobile/NativeVideosScreen'));
import { parseAppPath } from '../utils/storyLinks';
import './HomePage.css';

function NativeHome() {
  const { tab, setTab } = useNativeTab();
  const [openStoryId, setOpenStoryId] = useState(null);
  const [openEditionDate, setOpenEditionDate] = useState(null);
  const [storyTick, setStoryTick] = useState(0);
  const [searchOpen, setSearchOpen] = useState(false);

  useEffect(() => {
    if (isNativeApp() && /^\/story\//.test(window.location.pathname)) {
      window.history.replaceState({}, '', '/');
    }
  }, []);

  useEffect(() => {
    if (tab !== 'home') setOpenStoryId(null);
  }, [tab]);

  useEffect(() => {
    const close = () => setOpenStoryId(null);
    const onHide = () => {
      if (document.visibilityState === 'hidden') close();
    };
    window.addEventListener('rk-close-story', close);
    document.addEventListener('visibilitychange', onHide);
    return () => {
      window.removeEventListener('rk-close-story', close);
      document.removeEventListener('visibilitychange', onHide);
    };
  }, []);

  useEffect(() => {
    const parsed = parseAppPath(window.location.pathname);
    if (parsed.type === 'story') {
      if (isNativeApp()) {
        window.history.replaceState({}, '', '/');
        return;
      }
      setOpenStoryId(parsed.storyId);
      setStoryTick((n) => n + 1);
      setTab('home');
    }
    if (parsed.type === 'edition') {
      setOpenEditionDate(parsed.editionDate);
      setTab('paper');
    }
  }, [setTab]);

  useEffect(() => {
    const onStory = (e) => {
      const id = e.detail?.id;
      if (!id) return;
      setOpenStoryId(id);
      setStoryTick((n) => n + 1);
      setTab('home');
    };
    window.addEventListener('rk-open-story', onStory);
    return () => window.removeEventListener('rk-open-story', onStory);
  }, [setTab]);

  const openStory = (id) => {
    setOpenStoryId(id);
    setStoryTick((n) => n + 1);
  };

  return (
    <div className="home-page home-page--native" id="top">
      <NativeHeader onOpenSearch={() => setSearchOpen(true)} />
      <main className="ios-main" id="main-content">
        <div className={`ios-screen${tab === 'home' ? ' is-active' : ''}`} hidden={tab !== 'home'}>
          <NativeHomeScreen onOpenStory={openStory} />
        </div>
        <div className={`ios-screen rk-paper${tab === 'paper' ? ' is-active' : ''}`} hidden={tab !== 'paper'}>
          <NativePaperScreen active={tab === 'paper'} openEditionDate={openEditionDate} />
        </div>
        <div className={`ios-screen${tab === 'stories' ? ' is-active' : ''}`} hidden={tab !== 'stories'}>
          <Suspense fallback={null}>
            <NativeVideosScreen />
          </Suspense>
        </div>
        <div className={`ios-screen${tab === 'you' ? ' is-active' : ''}`} hidden={tab !== 'you'}>
          <NativeYouScreen onOpenStory={openStory} onOpenSearch={() => setSearchOpen(true)} />
        </div>
      </main>
      <NativeSearchSheet
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        onOpenStory={openStory}
      />
      <div className="toi-reader-host" aria-hidden={!openStoryId}>
        {openStoryId ? (
          <ArticlesSection
            key={storyTick}
            initialStoryId={openStoryId}
            onClose={() => setOpenStoryId(null)}
          />
        ) : null}
      </div>
      <div data-sub-free>
        <BottomNav />
      </div>
    </div>
  );
}

function WebHome() {
  const { t } = useLang();
  const [archivePdf, setArchivePdf] = useState(null);
  const [openStoryId, setOpenStoryId] = useState(null);
  const [openEditionDate, setOpenEditionDate] = useState(null);

  useEffect(() => {
    const parsed = parseAppPath(window.location.pathname);
    if (parsed.type === 'story') setOpenStoryId(parsed.storyId);
    if (parsed.type === 'edition') setOpenEditionDate(parsed.editionDate);
  }, []);

  useEffect(() => {
    const preload = () => {
      import('pdfjs-dist').catch(() => {});
    };
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(preload, { timeout: 2500 });
    } else {
      setTimeout(preload, 1200);
    }
  }, []);

  return (
    <div className="home-page" id="top">
      <a href="#main-content" className="skip-link">
        {t.skipToContent || 'Skip to content'}
      </a>
      <Header />
      <GuestSignInBar />

      {import.meta.env.DEV && (
        <div className="dev-banner" data-sub-free>
          Local dev — full access enabled for testing
        </div>
      )}

      <SiteAnnouncement />
      <MarketingPromoBar />
      <EditionLiveBanner />

      {archivePdf && (
        <PaperViewer pdfPath={archivePdf} dateLabel="" onClose={() => setArchivePdf(null)} />
      )}

      <main className="main-layout" id="main-content">
        <div className="main-content">
          <AdBannerSection placement="desktop" />
          <HeroSection openEditionDate={openEditionDate} />
          <SubscribePromoSection />
          <ArticlesSection initialStoryId={openStoryId} />
          <Suspense fallback={<div className="yt-loading" data-sub-free>{t.loadingVideos || t.loading}</div>}>
            <YoutubeSection />
          </Suspense>
        </div>
        <div className="sidebar-col">
          <Sidebar />
        </div>
      </main>

      <TrustRibbon />
      <Footer />
      <div data-sub-free>
        <BottomNav />
      </div>
      <AppDock />
      <ReferralPopup />
      <EditionNotifyPrompt />
    </div>
  );
}

function HomePageInner() {
  if (isNativeApp()) {
    return (
      <NativeTabProvider>
        <NativeHome />
      </NativeTabProvider>
    );
  }
  return <WebHome />;
}

export default function HomePage() {
  return (
    <SubscriptionGateProvider>
      <HomePageInner />
    </SubscriptionGateProvider>
  );
}
